// SPDX-License-Identifier: AGPL-3.0-or-later
import Accessibility from '@app/features/accessibility/state/Accessibility';
import styles from '@app/features/konvo_assistant/components/AssistantThinking.module.css';
import AssistantActivity from '@app/features/konvo_assistant/state/AssistantActivity';
import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {StopIcon} from '@app/features/ui/icons/Icons';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

// The still mascot paths are shared by the suggestion strip and calendar cards.
export const LEFT_REST =
	'M239 73 C249 67 262 75 262 88 L262 145 C262 163 252 176 234 181 C191 193 166 225 168 259 C169 283 180 303 195 319 C200 325 195 331 188 329 L132 311 C105 302 90 282 90 253 L90 197 C90 170 103 150 125 136 Z';
export const RIGHT_REST =
	'M287 170 C281 164 284 158 293 158 C365 155 422 199 422 261 L422 310 C422 338 409 358 384 373 L273 439 C261 446 249 439 249 425 L249 369 C249 349 260 334 279 328 C326 313 345 282 342 249 C340 219 320 189 287 170 Z';

export const AssistantThinking = observer(() => {
	const [systemReduced, setSystemReduced] = useState(
		() => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
	);
	useEffect(() => {
		const media = window.matchMedia('(prefers-reduced-motion: reduce)');
		const update = () => setSystemReduced(media.matches);
		media.addEventListener('change', update);
		update();
		return () => media.removeEventListener('change', update);
	}, []);
	const still = Accessibility.useReducedMotion || systemReduced;
	// Ticks with the thinking indicator's own poll, so an expired label fades back
	// to "thinking" without its own timer.
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(timer);
	}, []);
	const activity = AssistantActivity.current(now);
	const {t} = useLingui();
	const [stopping, setStopping] = useState(false);
	useEffect(() => {
		if (!AssistantActivity.running) setStopping(false);
	}, [AssistantActivity.running]);
	const stop = () => {
		setStopping(true);
		void KonvoConnections.cancelAssistantTurn().catch(() => setStopping(false));
	};
	return (
		<div
			className={clsx(styles.thinking, still && styles.still)}
			role="status"
			aria-live="polite"
			data-flx="konvo-assistant.thinking"
		>
			<svg className={styles.mascot} viewBox="-30 -30 572 572" aria-hidden="true" focusable="false">
				<path fill="#9981F4" d={LEFT_REST} />
				<path fill="#FFA16F" d={RIGHT_REST} />
			</svg>
			<span className={styles.label}>
				{activity ? <Trans>Konvo is {activity}</Trans> : <Trans>Konvo is thinking</Trans>}
			</span>
			<span className={styles.dots} aria-hidden="true">
				<i />
				<i />
				<i />
			</span>
			{AssistantActivity.running && (
				<button
					type="button"
					className={styles.stop}
					onClick={stop}
					disabled={stopping}
					aria-label={t`Stop the assistant`}
					title={t`Stop`}
					data-flx="konvo-assistant.thinking.stop"
				>
					<StopIcon size={remFromPx(10)} weight="fill" aria-hidden="true" />
					<Trans>Stop</Trans>
				</button>
			)}
		</div>
	);
});
